import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Shield, Database, Eye, Scale, ArrowRight, CheckCircle } from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.5 } }),
};

const SERVICES = [
  { icon: Scale, title: "AI Governance Framework Development", desc: "Policy, risk, and compliance architecture for AI systems" },
  { icon: Shield, title: "DoD & National Security AI Advisory", desc: "Mission-aligned AI integration for defense-adjacent organizations" },
  { icon: Database, title: "Enterprise AI Risk Assessment", desc: "Identifying and mitigating AI-related organizational risks" },
  { icon: Eye, title: "AI Ethics & Responsible Use Consulting", desc: "Building institutional guardrails that enable innovation without recklessness" },
];

const DIFFERENTIATORS = [
  { title: "Military intelligence background", desc: "Trained to operate in high-stakes, information-dense environments where bad governance has real consequences" },
  { title: "CISSP-certified", desc: "Deep information security expertise that most AI governance advisors lack" },
  { title: "Practitioner, not pundit", desc: "Dr. Magee builds systems, not just frameworks" },
];

export default function ReformIntelligence() {
  return (
    <div>
      {/* HERO */}
      <SEO
        title="Reform Intelligence"
        description="Reform Intelligence — AI governance firm founded by Dr. Ben Magee, serving enterprise and DoD-adjacent clients navigating AI policy, security, and operational implications at scale."
        keywords="Reform Intelligence, AI governance, DoD AI advisory, CISSP, enterprise AI risk, AI ethics consulting, Dr. Ben Magee"
        page="reform-intelligence"
        canonical="https://drbenmagee.com/reform-intelligence"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="reform-hero-title">
            AI Governance for the Missions That Matter Most
          </h1>
          <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C]">
            Reform Intelligence&reg;
          </p>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-32 bg-white" data-testid="reform-overview">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Overview</p>
          <div className="text-base text-[#1B2A4A]/70 leading-relaxed space-y-6">
            <p>
              Reform Intelligence&reg; is a technology and AI governance firm founded by Dr. Ben Magee, serving enterprise and DoD-adjacent clients navigating the policy, security, and operational implications of artificial intelligence at scale.
            </p>
            <p>
              At a moment when AI is moving faster than governance frameworks can keep pace, Reform Intelligence&reg; provides the analytical rigor of military intelligence, the technical depth of a CISSP-certified practitioner, and the leadership experience of a serial entrepreneur.
            </p>
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-20 md:py-32 bg-[#E8EDF5]" data-testid="reform-services">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Core Service Areas</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            What We Deliver
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                className="bg-white border border-[#1B2A4A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300"
              >
                <s.icon className="h-8 w-8 text-[#C9A84C] mb-4" />
                <h3 className="font-heading font-semibold text-xl text-[#1B2A4A] mb-2">{s.title}</h3>
                <p className="text-sm text-[#1B2A4A]/70 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY REFORM */}
      <section className="py-20 md:py-32 bg-white" data-testid="reform-differentiators">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Why Reform Intelligence&reg;</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            The Difference
          </h2>
          <div className="space-y-6">
            {DIFFERENTIATORS.map((d, i) => (
              <motion.div
                key={d.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="flex items-start gap-4 bg-[#F5EDD6] border border-[#C9A84C]/20 rounded-sm p-6"
              >
                <CheckCircle className="h-6 w-6 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-semibold text-base text-[#1B2A4A] mb-1">{d.title}</h3>
                  <p className="text-sm text-[#1B2A4A]/70">{d.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1B2A4A]" data-testid="reform-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Ready to Build a Governance Framework That Works?
          </h2>
          <p className="text-sm text-white/60 mb-8 max-w-2xl mx-auto">
            DoD-adjacent engagements handled under appropriate confidentiality protocols.
          </p>
          <Link to="/contact">
            <Button data-testid="reform-inquire-btn" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8 py-3 h-auto text-base">
              Inquire About an Engagement <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
